export default function ProcessSection() {
  const steps = [
    {
      number: '01',
      title: 'Contact Us',
      description: 'Call us at (915) 206-5814 or fill out our online form. We\'ll schedule a convenient time to discuss your inherited property situation. This initial consultation is completely free and there\'s no obligation to move forward.',
      icon: 'ri-phone-line'
    },
    {
      number: '02',
      title: 'Property Assessment',
      description: 'We\'ll visit the inherited property in El Paso to assess its condition. Unlike traditional buyers, we don\'t require any repairs or cleaning. We evaluate the property as-is and consider all factors including location, condition, and market value.',
      icon: 'ri-home-4-line'
    },
    {
      number: '03',
      title: 'Fair Cash Offer',
      description: 'Within 24 hours of viewing the property, we\'ll present you with a fair, no-obligation cash offer. Our offers are based on current El Paso market conditions and the property\'s actual value. There are no hidden fees or commissions.',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      number: '04',
      title: 'Paperwork Handling',
      description: 'This is where we truly shine. Our team handles all the complex paperwork, including probate documents, title transfers, and any legal requirements. We work with experienced probate attorneys to ensure everything is done correctly and efficiently.',
      icon: 'ri-file-text-line'
    },
    {
      number: '05',
      title: 'Flexible Closing',
      description: 'You choose the closing date that works best for your situation. Whether you need to close quickly or need more time to sort through belongings, we accommodate your timeline. We can close in as little as 7 days or wait several months if needed.',
      icon: 'ri-calendar-check-line'
    },
    {
      number: '06',
      title: 'Get Paid',
      description: 'On closing day, you receive your cash payment. No waiting for bank approvals or buyer financing. The money is transferred directly to you or distributed among heirs according to the estate plan. It\'s that simple.',
      icon: 'ri-hand-coin-line'
    }
  ];

  return (
    <section id="process" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-6">
            <span className="text-teal-700 text-sm font-semibold">Simple Process</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How We Buy Your Inherited Home
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our streamlined process makes selling your inherited El Paso property simple and stress-free. Here's exactly what to expect when you work with us.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <i className={`${step.icon} text-3xl text-white`}></i>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="text-6xl font-bold text-teal-100 absolute top-4 right-8">
                    {step.number}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 relative z-10">
                    {step.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed relative z-10">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl shadow-2xl overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="p-12 text-white">
              <h3 className="text-3xl font-bold mb-6">
                Why Our Process Works Better
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">No Real Estate Agent Needed</h4>
                    <p className="text-teal-50">Save thousands in commission fees. We buy directly from you, eliminating the middleman and putting more money in your pocket.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">No Repairs or Cleaning Required</h4>
                    <p className="text-teal-50">We buy inherited homes in any condition. Don't spend time and money fixing up a property you're planning to sell anyway.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">We Handle All Legal Paperwork</h4>
                    <p className="text-teal-50">Our team manages probate documents, title transfers, and all legal requirements. You don't need to become an expert in Texas property law.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Fast, Guaranteed Cash Payment</h4>
                    <p className="text-teal-50">No waiting for buyer financing or dealing with deals falling through. We pay cash and can close on your schedule.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative h-96 lg:h-auto">
              <img
                src="https://public.readdy.ai/ai/img_res/5d27a08c9a322151791bf2793fd81346.jpg"
                alt="Successful Inherited Home Sale"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}